import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { DropResult } from 'react-smooth-dnd'
import { Box, Stack, Typography } from '@mui/material'
import useLocationHelpers from '@/hooks/useLocationHelpers'
import { useGetWorkflowStateTypes } from '@/service-library/hooks/workflow-state-types'
import {
  useGetWorkflowStates,
  useUpdateWorkflowStates,
} from '@/service-library/hooks/workflow-states'
import { useUpdateWorkflow } from '@/service-library/hooks/workflows'
import queryKeys from '@/service-library/query-keys'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { WorkflowState } from '@/types/workflows'
import { sortBySortOrder } from '@/utils/field'
import { getWorkflowStatesByType } from '@/utils/workflow-states'
import CopyIDButton from '@/components/copy-id-button/CopyIDButton'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import WorkflowStatePage from '@/components/workflow-state-page/WorkflowStatePage'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import { useWorkflowsContext } from '@/components/workflows-provider/WorkflowsProvider'
import WorkflowStatesList from './WorkflowStatesList'

export default function WorkflowStatesPage() {
  const { '*': id, projectId } = useParams()
  const navigate = useNavigate()
  const isSuperUser = useIsSuperUser()
  const stateContainerRef = useRef<HTMLDivElement>(null)

  const { selectedWorkflow } = useSelectedWorkflowContext()
  const { queryKey: workflowsListQueryKey } = useWorkflowsContext()
  const { updateWorkflow } = useUpdateWorkflow({
    listQueryKey: workflowsListQueryKey,
  })
  const { appendCurrentParamsToUrl } = useLocationHelpers()

  const [isDragging, setIsDragging] = useState(false)

  const { workflowStates: savedWorkflowStates, queryKey } =
    useGetWorkflowStates({
      filters: {
        limit: '1000',
        workflow_id: selectedWorkflow.id,
        fields__include: 'excluded_project_grid_fields_ids,workflow_state_type', // including since we will use it as initialData
      },
      staleTime: 0, // Prevent firing off this request since we have initialData
      initialListData: selectedWorkflow.workflow_states,
    })

  const { workflowStateTypes } = useGetWorkflowStateTypes()
  const sortedWorkflowStateTypes = sortBySortOrder(workflowStateTypes)

  const displayableWorkflowStates = useMemo(() => {
    return (
      savedWorkflowStates
        ?.filter(({ code }) => code !== 'processing')
        .toSorted((a, b) => a.sort_order - b.sort_order) || []
    )
  }, [savedWorkflowStates])

  const [statesByType, setStatesByType] = useState<
    Record<string, WorkflowState[]>
  >({})

  useEffect(() => {
    setStatesByType(getWorkflowStatesByType(displayableWorkflowStates))
  }, [displayableWorkflowStates])

  const selectedState = useMemo(
    () => displayableWorkflowStates.find((state) => state.id === id),
    [displayableWorkflowStates, id],
  )

  const { updateWorkflowStates } = useUpdateWorkflowStates({
    listQueryKey: queryKey,
    sideEffectQueryKeys: [queryKeys.workflows.all],
    useSortOrder: true,
    onError: () => {
      // If we fail to update the workflow states, we should reset the state to the saved state
      const firstState = displayableWorkflowStates[0]
      if (
        firstState &&
        firstState.id !== selectedWorkflow.initial_workflow_state_id
      ) {
        updateWorkflow({
          ...selectedWorkflow,
          initial_workflow_state_id: firstState.id,
        })
      }
    },
  })

  const readyForUpdateRef = useRef(false)

  const onCreateWorkflowState = useCallback(
    (newState: WorkflowState) => {
      const statesToUpdate = displayableWorkflowStates
        .filter(
          (state) =>
            state.id !== newState.id && state.sort_order >= newState.sort_order,
        )
        .map((state) => ({
          ...state,
          sort_order: state.sort_order + 1,
        }))
      if (statesToUpdate.length > 0) {
        updateWorkflowStates(statesToUpdate)
      }
    },
    [displayableWorkflowStates, updateWorkflowStates],
  )

  const getNewStateSortOrder = useCallback(
    (code: string) => {
      const currentTypeIndex = sortedWorkflowStateTypes.findIndex(
        (t) => t.code === code,
      )

      // Create an ordered list of relevant state types (current type + previous types)
      const orderedTypes = [
        ...sortedWorkflowStateTypes.slice(0, currentTypeIndex + 1),
      ].reverse()

      // Find the first type that actually has states
      const typeWithStates = orderedTypes.find(
        (type) => statesByType[type.code]?.length,
      )
      if (typeWithStates) {
        const states = statesByType[typeWithStates.code]
        // @ts-expect-error -- we should have a state
        return states.at(-1).sort_order + 1
      }

      // Fallback: Check 'unassigned' or default to 0
      return statesByType.unassigned?.length
        ? // @ts-expect-error -- we should have a state
          statesByType.unassigned.at(-1).sort_order + 1
        : 0
    },
    [sortedWorkflowStateTypes, statesByType],
  )

  const onDrop = (
    { removedIndex, addedIndex, payload }: DropResult,
    code = 'unassigned',
  ) => {
    if (removedIndex === null && addedIndex === null) return
    const { workflowState } = payload
    const statesForCode = [...(statesByType[code] || [])]

    if (removedIndex !== null) {
      statesForCode.splice(removedIndex, 1)
    }
    if (addedIndex !== null) {
      const workflowStateType = workflowStateTypes.find(
        (type) => type.code === code,
      )
      statesForCode.splice(addedIndex, 0, {
        ...workflowState,
        workflow_state_type: workflowStateType,
        workflow_state_type_id: workflowStateType?.id || null,
      })
    }

    readyForUpdateRef.current = true
    setStatesByType((prev) => ({
      ...prev,
      [code]: statesForCode,
    }))
  }

  useEffect(() => {
    if (readyForUpdateRef.current) {
      let updatedStateList = sortedWorkflowStateTypes.reduce<WorkflowState[]>(
        (acc, type) => {
          return [...acc, ...(statesByType[type.code] || [])]
        },
        [...(statesByType.unassigned || [])],
      )
      updatedStateList = updatedStateList.map((state, index) => ({
        ...state,
        sort_order: index,
      }))
      updateWorkflowStates(updatedStateList)
      const newFirstStateId = updatedStateList[0]?.id
      if (
        newFirstStateId &&
        newFirstStateId !== selectedWorkflow.initial_workflow_state_id
      ) {
        // We should be able to make both calls in parallel, but if there is an error
        // reordering we have logic that rolls back this change.
        updateWorkflow({
          ...selectedWorkflow,
          initial_workflow_state_id: newFirstStateId,
        })
      }
      readyForUpdateRef.current = false
    }
  }, [
    statesByType,
    sortedWorkflowStateTypes,
    updateWorkflowStates,
    selectedWorkflow,
    updateWorkflow,
  ])

  useEffect(() => {
    if (displayableWorkflowStates[0]?.id && (!id || !selectedState)) {
      navigate(
        appendCurrentParamsToUrl(
          `/projects/${projectId}/settings/workflow-states/${displayableWorkflowStates[0].id}`,
        ),
      )
    }
  }, [
    displayableWorkflowStates,
    id,
    navigate,
    appendCurrentParamsToUrl,
    selectedState,
    projectId,
  ])

  useEffect(() => {
    if (stateContainerRef.current) {
      stateContainerRef.current.scrollTop = 0
    }
  }, [id])

  return (
    <>
      <ProjectBreadcrumb label="Workflow States" url="../../workflow-states" />
      <Stack
        direction="row"
        spacing={4}
        sx={{ m: -2, mt: -3, overflow: 'auto' }}
      >
        <Stack
          spacing={1}
          flexShrink={0}
          sx={{
            width: '320px',
            minWidth: 0,
            overflowY: 'auto',
            px: 2,
            pb: 2,
            pt: 3,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5">{selectedWorkflow.name}</Typography>
            {isSuperUser && (
              <CopyIDButton
                stringToCopy={selectedWorkflow.id}
                asIconButton
                isSuperUser
                size="small"
              />
            )}
          </Stack>

          <Box>
            {statesByType.unassigned?.length > 0 && (
              <WorkflowStatesList
                workflowStates={statesByType.unassigned}
                onDrop={onDrop}
                isDragging={isDragging}
                setIsDragging={setIsDragging}
                listQueryKey={queryKey}
              />
            )}
            {sortedWorkflowStateTypes.map((type) => (
              <WorkflowStatesList
                key={type.id}
                workflowStates={statesByType[type.code]}
                workflowStateType={type}
                onDrop={(dropResult) => onDrop(dropResult, type.code)}
                isDragging={isDragging}
                setIsDragging={setIsDragging}
                listQueryKey={queryKey}
                onCreateWorkflowState={onCreateWorkflowState}
                newWorkflowStateSortOrder={getNewStateSortOrder(type.code)}
              />
            ))}
          </Box>
        </Stack>

        {id && (
          <Box sx={{ overflowY: 'auto', flexGrow: 1 }} ref={stateContainerRef}>
            <WorkflowStatePage
              workflowStateId={id}
              workflowState={selectedState}
              listQueryKey={queryKey}
            />
          </Box>
        )}
      </Stack>
    </>
  )
}
